import { Option } from "@inrev/core";
import { AdminSuretyBond } from "@portal/api/bond/admin";
import { UseFollowersReturn } from "@portal/api/followers/types";
import { EntityTags } from "@portal/components/admin/EntityTags";
import { Icon, Text } from "@portal/components/primitives";
import { Separator } from "@portal/components/ui/Separator";
import { getStatusTagByBondStatus } from "@portal/components/ui/StatusTag";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { carrierLogoSmallMap, suretyBondClosedReasonTypeLabelMap } from "../../../../constants";
import { formatBondMediumDate } from "../../../shared/bond/bondHeaderTerms";
import { AdminAssigneeInlineControls } from "../../shared/AdminAssigneeInlineControls";
import { AdminFollowerControls } from "../../shared/AdminFollowerControls";

export type BondCardProps = {
	bond: AdminSuretyBond;
	actionButtons?: ReactNode;
	followersControl: UseFollowersReturn;
};

const formatEffectiveSpan = (effectiveDate: string, expirationDate: Option<string>) => {
	const effective = formatBondMediumDate(effectiveDate);
	return Option.mapOrElse(
		expirationDate,
		(expiration) => `Effective ${effective} to ${formatBondMediumDate(expiration)}`,
		`Effective ${effective}`,
	);
};

export const BondCard = ({ bond, actionButtons, followersControl }: BondCardProps) => {
	const statusTag = getStatusTagByBondStatus(bond.status);
	const dateLine =
		bond.contractSuretyType === "bid"
			? `Bid Date ${formatBondMediumDate(bond.bidDate)}`
			: formatEffectiveSpan(bond.effectiveDate, bond.expirationDate);

	return (
		<div
			className="flex w-full flex-col gap-2"
			data-testid="admin-bond-header"
			id="bond_request_card"
		>
			<div className="flex items-center justify-between gap-4">
				{statusTag}
				<div className="flex min-w-0 items-center gap-3">
					<div className="flex min-w-0 items-center gap-[10px]">
						{carrierLogoSmallMap[bond.carrierName]}
						<Text type="caption" color="secondary" truncate className="min-w-0">
							{bond.carrierDisplayName}
						</Text>
					</div>
					<Separator orientation="vertical" className="h-4 bg-bc-default" />
					<Text type="caption" color="secondary" className="shrink-0 font-mono">
						{bond.number}
					</Text>
				</div>
			</div>
			<div className="flex flex-col gap-3">
				<div className="flex items-start gap-6">
					<div className="flex min-w-0 flex-1 flex-col gap-1.5">
						<Text type="display" color="primary" className="inline-flex">
							{bond.displayTitle}
						</Text>
						<div className="flex items-baseline gap-x-1">
							<Text type="body" color="secondary">
								for
							</Text>
							<Link to={`/accounts/${bond.accountId}`}>
								<Text type="body" color="secondary" className="hover:underline">
									{bond.principalName}
								</Text>
							</Link>
						</div>
						<div className="flex items-center gap-[6px]">
							<Icon type="calendar" size="mini" color="tertiary" />
							<Text type="body" color="tertiary">
								{dateLine}
							</Text>
						</div>
						{bond.status === "closed" && Option.isSome(bond.closedAt) && (
							<div className="flex items-center gap-[7px]">
								<Icon type="shield-slash" size="mini" color="secondary" />
								<Text type="body" color="secondary">
									Closed {formatBondMediumDate(bond.closedAt)}
									{bond.closedReason
										? ` - ${suretyBondClosedReasonTypeLabelMap[bond.closedReason]}`
										: ""}
								</Text>
							</div>
						)}
					</div>
					{actionButtons && <div className="flex w-[148px] shrink-0 flex-col">{actionButtons}</div>}
				</div>
				<div data-testid="admin-bond-header-people" className="flex w-full items-center gap-6">
					<AdminAssigneeInlineControls
						assignedEntity={{ entityType: "bond", entityId: bond.id }}
						currentAssignee={bond.underwriterUserId}
						trackingId="bond-admin-assign-underwriter"
						className="pt-0"
						avatarClassName="bg-surface-neutral"
					/>
					<AdminFollowerControls
						{...followersControl}
						appearance="avatars"
						manageFollowersSubtext="Followers will receive email notifications for all comments and changes to the status of this bond"
						agencyId={bond.agencyId}
						trackingPrefix="bond-admin-header-follow"
						className="pt-0"
					/>
				</div>
				<div data-testid="admin-bond-header-tags" className="w-full">
					<EntityTags entityType="bond" entityId={bond.id} accountId={bond.accountId} />
				</div>
			</div>
		</div>
	);
};
